/**
 * AJ2 · Entrega — cuenta atrás hasta la hora de corte y los tres pasos del
 * envío con fecha (Pedido realizado · En camino · Entrega estimada).
 *
 * Todo se calcula con el reloj de pared de Madrid (Europe/Madrid), pase lo
 * que pase con la zona horaria del navegador: la hora de corte es la del
 * almacén. El reloj de Madrid se representa como una fecha UTC «falsa»
 * (Date.UTC con los campos de Madrid) para poder sumar días y leer el día
 * de la semana sin que el navegador aplique su propio huso. En los dos
 * cambios de hora del año puede desfasarse una hora esa noche; no afecta a
 * una cuenta atrás que solo se enseña antes de las 14:00.
 *
 * Marcado en blocks/aj2-entrega.liquid (ahí está la regla de cada paso). Sin
 * JavaScript se ven las fechas de reserva que calcula el Liquid y no hay
 * cuenta atrás.
 */

const DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const MESES_CORTOS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sept', 'oct', 'nov', 'dic'];
const DIA_MS = 86400000;

const formato = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Madrid',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

/** Ahora mismo en Madrid, como milisegundos «de pared» (UTC falso). */
function ahoraMadrid() {
  const p = {};
  for (const parte of formato.formatToParts(new Date())) p[parte.type] = parte.value;
  return Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour % 24, +p.minute, +p.second);
}

const inicioDelDia = (ms) => Math.floor(ms / DIA_MS) * DIA_MS;
const claveDia = (ms) => new Date(ms).toISOString().slice(0, 10);
const dos = (n) => String(n).padStart(2, '0');

class Aj2Entrega extends HTMLElement {
  connectedCallback() {
    const [h, m] = (this.dataset.corte || '14:00').split(':').map((n) => parseInt(n, 10));
    this.corteMs = ((isNaN(h) ? 14 : h) * 60 + (isNaN(m) ? 0 : m)) * 60000;
    this.min = Math.max(1, parseInt(this.dataset.min, 10) || 2);
    this.max = Math.max(this.min, parseInt(this.dataset.max, 10) || this.min);
    this.festivos = new Set(
      (this.dataset.festivos || '')
        .split(',')
        .map((f) => f.trim())
        .filter(Boolean)
    );
    this.titulo = this.querySelector('[data-aj2-entrega-titulo]');
    this.sub = this.querySelector('[data-aj2-entrega-sub]');
    this.pasos = {
      pedido: this.querySelector('[data-aj2-paso="pedido"]'),
      camino: this.querySelector('[data-aj2-paso="camino"]'),
      entrega: this.querySelector('[data-aj2-paso="entrega"]'),
    };
    this.reloj = this.querySelector('[data-aj2-entrega-reloj]');
    this.cifras = {
      h: this.querySelector('[data-u="h"]'),
      m: this.querySelector('[data-u="m"]'),
      s: this.querySelector('[data-u="s"]'),
    };
    this.diaPintado = null;
    /* Nombres de días y meses en el idioma de la tienda (locales del tema). */
    const lista = (valor, respaldo) => {
      const partes = (valor || '').split(',').map((p) => p.trim());
      return partes.length === respaldo.length ? partes : respaldo;
    };
    this.dias = lista(this.dataset.dias, DIAS);
    this.meses = lista(this.dataset.meses, MESES_CORTOS);

    this.iniciarEnvio();
    this.tic();
    this.intervalo = window.setInterval(() => this.tic(), 1000);
  }

  disconnectedCallback() {
    window.clearInterval(this.intervalo);
    if (this.observador) this.observador.disconnect();
  }

  esLaborable(diaMs) {
    const semana = new Date(diaMs).getUTCDay();
    return semana >= 1 && semana <= 5 && !this.festivos.has(claveDia(diaMs));
  }

  siguienteLaborable(diaMs) {
    let d = diaMs;
    do {
      d += DIA_MS;
    } while (!this.esLaborable(d));
    return d;
  }

  sumarLaborables(diaMs, n) {
    let d = diaMs;
    for (let i = 0; i < n; i++) d = this.siguienteLaborable(d);
    return d;
  }

  /* «23 sept» */
  corta(ms) {
    const f = new Date(ms);
    return `${f.getUTCDate()} ${(this.meses || MESES_CORTOS)[f.getUTCMonth()]}`;
  }

  /* «23 sept», «23-24 sept» o «30 sept-2 oct» */
  rango(a, b) {
    if (a === b) return this.corta(a);
    const fa = new Date(a);
    const fb = new Date(b);
    if (fa.getUTCMonth() === fb.getUTCMonth()) return `${fa.getUTCDate()}-${this.corta(b)}`;
    return `${this.corta(a)}-${this.corta(b)}`;
  }

  /* «lunes 28» */
  textoDia(ms) {
    const f = new Date(ms);
    return `${(this.dias || DIAS)[f.getUTCDay()]} ${f.getUTCDate()}`;
  }

  tic() {
    const ahora = ahoraMadrid();
    const hoy = inicioDelDia(ahora);
    const corte = hoy + this.corteMs;
    const aTiempo = this.esLaborable(hoy) && ahora < corte;
    const salida = aTiempo ? hoy : this.siguienteLaborable(hoy);

    /* Titular y pasos: solo se repintan cuando cambia el día (de hoy o de salida). */
    const clave = `${hoy}|${salida}`;
    if (this.diaPintado !== clave) {
      this.diaPintado = clave;
      let camino = salida;
      if (salida === hoy && this.min > 1) camino = this.siguienteLaborable(hoy);
      const desde = this.sumarLaborables(salida, this.min);
      const hasta = this.sumarLaborables(salida, this.max);
      if (this.pasos.pedido) this.pasos.pedido.textContent = this.corta(hoy);
      if (this.pasos.camino) this.pasos.camino.textContent = this.corta(camino);
      if (this.pasos.entrega) this.pasos.entrega.textContent = this.rango(desde, hasta);
      if (this.titulo) {
        if (aTiempo) {
          this.titulo.textContent = (this.dataset.tituloAntes || 'Pídelo antes de las [hora]').replace('[hora]', this.dataset.corte || '14:00');
        } else {
          const dia =
            salida - hoy === DIA_MS
              ? this.dataset.manana || 'mañana'
              : (this.dataset.elDia || 'el [dia]').replace('[dia]', this.textoDia(salida));
          this.titulo.textContent = (this.dataset.tituloDespues || 'Pídelo ahora y sale [dia]').replace('[dia]', dia);
        }
      }
      if (this.sub) this.sub.hidden = !aTiempo;
      this.classList.toggle('aj2-entrega--a-tiempo', aTiempo);
    }

    if (!this.reloj) return;
    if (!aTiempo) {
      this.reloj.hidden = true;
      return;
    }
    const resto = Math.max(0, Math.floor((corte - ahora) / 1000));
    this.cifras.h.textContent = dos(Math.floor(resto / 3600));
    this.cifras.m.textContent = dos(Math.floor((resto % 3600) / 60));
    this.cifras.s.textContent = dos(resto % 60);
    this.reloj.hidden = false;
  }

  /* Variante elegida (corregido el 26/09/2026). Antes se leía el PRIMER
     input marcado del selector: con una sola opción valía, pero con varias
     (Tamaño · Color · Cuántos tarros) ese input es el de Tamaño, y su
     `data-variant-id` solo se actualiza cuando llega el HTML nuevo del
     servidor. Si tardaba más de 200 ms, la barra se quedaba con la variante
     anterior. Ahora se lee el JSON de la variante que el selector lleva
     dentro, que el tema actualiza en el mismo momento que el precio. */
  varianteElegida() {
    const script = document.querySelector('.product-information variant-picker script[type="application/json"]');
    if (script) {
      try {
        const variante = JSON.parse(script.textContent);
        if (variante && variante.id) return String(variante.id);
      } catch (error) {
        /* JSON a medio morfar: se sigue con el respaldo. */
      }
    }
    const oculto = document.querySelector('.product-information form[action*="/cart/add"] [name="id"]');
    return oculto && oculto.value ? String(oculto.value) : null;
  }

  /* Envío gratis: el Liquid deja lo que falta por variante; aquí se repinta
     cada vez que el tema toca el selector (MutationObserver sobre la
     sección, filtrado a cambios dentro de `variant-picker`, para no
     dispararse con la cuenta atrás de este mismo bloque). Se agenda con
     setTimeout y no con rAF, que en una pestaña de fondo se congela. */
  iniciarEnvio() {
    const nodo = this.querySelector('[data-aj2-entrega-mapa]');
    if (!nodo) return;
    let mapa;
    try {
      mapa = JSON.parse(nodo.textContent);
    } catch (error) {
      return;
    }
    const barra = this.querySelector('[data-aj2-entrega-progreso]');
    const nota = this.querySelector('[data-aj2-entrega-nota]');

    let ultima = null;
    const pintar = () => {
      const id = this.varianteElegida();
      if (!id || id === ultima) return;
      const datos = mapa[id];
      if (!datos) return;
      ultima = id;
      if (barra) barra.style.width = `${datos.pct}%`;
      if (nota) nota.textContent = datos.texto;
    };

    const selector = document.querySelector('.product-information variant-picker');
    const raiz = (selector && selector.closest('.shopify-section')) || document.body;
    const tocaSelector = (registro) => {
      const n = registro.target.nodeType === 1 ? registro.target : registro.target.parentElement;
      if (n && n.closest('variant-picker')) return true;
      return Array.from(registro.addedNodes).some(
        (a) => a.nodeType === 1 && (a.matches('variant-picker') || a.querySelector('variant-picker'))
      );
    };
    let pendiente = false;
    this.observador = new MutationObserver((registros) => {
      if (pendiente || !registros.some(tocaSelector)) return;
      pendiente = true;
      window.setTimeout(() => {
        pendiente = false;
        pintar();
      }, 0);
    });
    this.observador.observe(raiz, { childList: true, subtree: true, characterData: true });

    pintar();
  }
}

if (!customElements.get('aj2-entrega')) customElements.define('aj2-entrega', Aj2Entrega);